import { Box, Button, Group, SegmentedControl, Select, Stack, TextInput } from "@mantine/core"
import { MagnifyingGlassIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"

export type ActiveQuickFilter = "ALL" | "VIP_URGENT" | "SLA_RISK" | "UNASSIGNED"

export interface OrderTypeOption {
  value: string | null
  label: string
}

interface Props {
  status: string
  onStatusChange: (next: string) => void
  statusOptions: { value: string; label: string }[]
  orderType: string | null
  onOrderTypeChange: (next: string | null) => void
  orderTypeOptions: OrderTypeOption[]
  quick: ActiveQuickFilter
  onQuickChange: (next: ActiveQuickFilter) => void
  counts: {
    all: number
    vipUrgent: number
    slaRisk: number
    unassigned: number
  }
  search: string
  onSearchChange: (value: string) => void
  onClear: () => void
}

const QUICK: ActiveQuickFilter[] = ["ALL", "VIP_URGENT", "SLA_RISK", "UNASSIGNED"]

/** Search + order type + quick chips, then the status segment (canvas Ord_DeliveryTabsA). */
export function ActiveFiltersToolbar({
  status,
  onStatusChange,
  statusOptions,
  orderType,
  onOrderTypeChange,
  orderTypeOptions,
  quick,
  onQuickChange,
  counts,
  search,
  onSearchChange,
}: Props) {
  const { t } = useTranslation("delivery")
  const quickCount: Record<ActiveQuickFilter, number> = {
    ALL: counts.all,
    VIP_URGENT: counts.vipUrgent,
    SLA_RISK: counts.slaRisk,
    UNASSIGNED: counts.unassigned,
  }

  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="wrap" align="flex-end">
        <Box flex={1} miw={240}>
          <TextInput
            aria-label={t("activeFilter.search")}
            placeholder={t("manage.active.searchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            leftSection={<MagnifyingGlassIcon size={16} />}
          />
        </Box>
        <Select
          aria-label={t("activeFilter.orderType")}
          leftSection={null}
          data={orderTypeOptions.map((o) => ({ value: o.value ?? "ALL", label: o.label }))}
          value={orderType ?? "ALL"}
          onChange={(v) => onOrderTypeChange(v === "ALL" || !v ? null : v)}
          w={220}
          allowDeselect={false}
        />
        {QUICK.map((q) => (
          <Button
            key={q}
            variant={quick === q ? "filled" : "default"}
            color={quick === q ? "dark" : undefined}
            onClick={() => onQuickChange(q)}
          >
            {t(`activeFilter.quick.${q}`, { count: quickCount[q] })}
          </Button>
        ))}
      </Group>
      <Box>
        <SegmentedControl
          value={status}
          onChange={(v) => onStatusChange(v)}
          data={statusOptions}
          radius="md"
        />
      </Box>
    </Stack>
  )
}
